import React, { useEffect, useState } from 'react';
import { Loader2, GitCompare } from 'lucide-react';
import { toast } from 'sonner';
import { compareFiles, getInfo } from '../../utils/api';
import { useT } from '../../i18n';
import SecondFileDropzone from './SecondFileDropzone';
import MissingChoice from './MissingChoice';

/**
 * What changed between the current file and a second one.
 *
 * The result becomes the current table, with a status column: everything else
 * in the app — filtering, exporting, saving — then works on the diff for free.
 */
const CompareOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [columns, setColumns] = useState([]);
  const [secondFile, setSecondFile] = useState(null);
  const [secondInfo, setSecondInfo] = useState(null);
  const [key, setKey] = useState([]);
  const [includeUnchanged, setIncludeUnchanged] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const loadColumns = async () => {
      try {
        const result = await getInfo();
        setColumns(result.info.column_names || []);
      } catch (error) {
        toast.error(t('form.loadColumnsFailed'));
      }
    };
    loadColumns();
  }, []);

  const handleUploaded = (result, file) => {
    setSecondFile(file);
    setSecondInfo(result.info);

    // A column named the same in both files is almost always the key. Guess it
    // rather than make the user hunt, but leave the choice visible.
    const shared = columns.filter((column) => (result.info.column_names || []).includes(column));
    const likely = shared.find((column) => /^(id|identifiant|code|reference|référence|sku)$/i.test(column));
    if (likely) setKey([likely]);
  };

  const toggleKey = (column) => {
    setKey((current) =>
      current.includes(column) ? current.filter((c) => c !== column) : [...current, column]
    );
  };

  const sharedColumns = columns.filter(
    (column) => (secondInfo?.column_names || []).includes(column)
  );

  const handleApply = async () => {
    if (key.length === 0) {
      toast.error(t('compare.needKey'));
      return;
    }
    setApplying(true);
    try {
      const result = await compareFiles({ key, include_unchanged: includeUnchanged });
      (result.warnings || []).forEach((warning) => toast.warning(warning, { duration: 8000 }));
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('compare.failed'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 text-muted-foreground">
        <GitCompare className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <p className="text-sm">{t('compare.intro')}</p>
      </div>

      {!secondFile ? (
        <SecondFileDropzone label={t('compare.uploadSecond')} onUploaded={handleUploaded} />
      ) : (
        <>
          <div className="card p-4 bg-emerald-50 border-emerald-200">
            <p className="text-sm font-medium text-emerald-800">{secondFile.name}</p>
            <p className="text-xs text-emerald-700 mt-1">
              {t('append.secondSummary', {
                rows: secondInfo?.rows?.toLocaleString(),
                columns: secondInfo?.columns,
              })}
            </p>
          </div>

          <div>
            <label className="label mb-2 block">{t('compare.keyLabel')}</label>
            <p className="text-xs text-muted-foreground mb-2">{t('compare.keyHelp')}</p>
            {sharedColumns.length === 0 ? (
              <p className="text-sm text-destructive">{t('compare.noSharedColumn')}</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                {sharedColumns.map((column) => (
                  <label key={column} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={key.includes(column)}
                      onChange={() => toggleKey(column)}
                      data-testid={`compare-key-${column}`}
                    />
                    <span className="text-sm truncate">{column}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeUnchanged}
              onChange={(e) => setIncludeUnchanged(e.target.checked)}
              className="mt-1"
              data-testid="compare-include-unchanged"
            />
            <div>
              <span className="text-sm font-medium">{t('compare.includeUnchanged')}</span>
              <p className="text-xs text-muted-foreground">{t('compare.includeUnchangedHelp')}</p>
            </div>
          </label>

          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="text-sm font-medium mb-2">{t('compare.resultTitle')}</h4>
            <p className="text-xs text-muted-foreground">{t('compare.resultHelp')}</p>
          </div>
        </>
      )}

      <MissingChoice reasons={[!secondFile && t('form.needSecondFile'), key.length === 0 && t('compare.needKey')]} />

      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <button onClick={onCancel} className="btn-secondary" data-testid="compare-cancel">
          {t('form.cancel')}
        </button>
        <button
          onClick={handleApply}
          disabled={applying || !secondFile || key.length === 0}
          className="btn-primary flex items-center gap-2"
          data-testid="compare-apply"
        >
          {applying && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('compare.apply')}
        </button>
      </div>
    </div>
  );
};

export default CompareOperation;
